import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { IServerSideDatasource, ValueGetterParams } from 'ag-grid-enterprise';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getCellRenderer, getValueGetter } from 'src/app/modules/core/components/ag-grid';
import { DataTableServerComponent } from 'src/app/modules/core/components/data-table-server/data-table.component';
import { GridHelperService } from 'src/app/modules/core/services/grid-helper.service';
import { PicklistService } from 'src/app/modules/core/services/picklist/picklist.service';
import {
  DocumentReader,
  FieldDataType,
  SchemaField,
  SystemFieldType,
  Workflow,
  WorkflowParentType
} from 'src/app/modules/core/store/models';
import * as fromRouting from 'src/app/modules/core/store/routing';
import * as fromWorkflow from 'src/app/modules/communications/store/workflow';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import { v4 as uuidv4 } from 'uuid';
import { WorkflowApiService } from 'src/app/modules/communications/store/workflow/workflow-api.service';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
  selector: 'app-workflow-list',
  templateUrl: './workflow-list.component.html',
  styleUrls: ['./workflow-list.component.scss'],
  standalone: false
})
export class WorkflowListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(DataTableServerComponent, { static: true }) dataTable: DataTableServerComponent;

  workflow: Workflow;
  workflowToDelete: Workflow;
  destroy$ = new Subject<void>();
  datasource: any;
  columDefs: any[] = [];
  skip = 0;
  take = 10;
  totalCount: number = 0;
  organizationUId: string;
  isTableActive: boolean;

  selectedParentType: WorkflowParentType = WorkflowParentType.MassNotification;
  parentTypes = [
    {
      value: WorkflowParentType.MassNotification,
      name: this.translateService.instant(`MANAGEMENT.WORKFLOW.PARENT-TYPE-${WorkflowParentType.MassNotification}`)
    },
    {
      value: WorkflowParentType.MassNotificationRecipient,
      name: this.translateService.instant(`MANAGEMENT.WORKFLOW.PARENT-TYPE-${WorkflowParentType.MassNotificationRecipient}`)
    }
  ];

  protected readonly userContext = inject(UserContextService);
  private notify = inject(SystemNotificationsService);

  constructor(
    private store: Store<fromWorkflow.State>,
    private translateService: TranslateService,
    private gridHelper: GridHelperService,
    private picklistService: PicklistService,
    private workflowApiService: WorkflowApiService
  ) {}

  ngOnInit(): void {
    if (!this.userContext.canReadWorkflow()) {
      this.notify.error(this.translateService.instant('COMMON.MISSING-PERMISSIONS'));
      return;
    }

    this.columDefs = this.generateColumns();
    this.datasource = this.getServerSideDatasource();

    this.store
      .select(fromWorkflow.selectIsTableChanged)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isChanged => {
        if (isChanged === true) {
          this.dataTable?.refreshGrid();
        }
      });

    this.store
      .select(fromAuth.selectUserContext)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userContext => {
        this.organizationUId = userContext?.organizationUId;
      });
  }

  ngAfterViewInit() {
    this.dataTable?.groupRows(false);
  }

  parentTypeChanged(event: any): void {
    this.skip = 0;
    this.dataTable?.refreshGrid();
  }

  refreshGrid(): void {
    this.dataTable?.refreshGrid();
  }

  getServerSideDatasource(): IServerSideDatasource {
    this.isTableActive = false;
    return {
      getRows: params => {
        const translatedParams = this.gridHelper.gridParamsToRequestParams(params.request, this.getFilterClause());
        this.workflowApiService.getWorkflows(translatedParams, false).subscribe(
          resp => {
            const response = this.gridHelper.queryResultToGridData(resp);
            this.totalCount = response?.rowCount;
            this.store.dispatch(fromWorkflow.setTableChanged({ changed: false }));
            params.success({
              rowData: response?.rowData,
              rowCount: response?.rowCount
            });
          },
          err => params.fail()
        );
      }
    };
  }

  private getFilterClause(): string {
    return `ParentType=${this.selectedParentType}`;
  }

  onPaginate(skip: number): void {
    this.skip = skip;
  }

  onTakeChanged(take: number): void {
    this.take = take;
  }

  private generateColumns() {
    var columns = [];

    const canEdit = this.userContext.canUpdateWorkflow();
    const canDelete = this.userContext.canDeleteWorkflow();

    if (canEdit || canDelete) {
      const actions = [];

      if (canEdit) {
        actions.push({
          label: this.translateService.instant(`MANAGEMENT.DATASETS.EDIT`),
          icon: 'icon-edit',
          click: data => {
            this.editClick(data);
          }
        });
      }

      if (canDelete) {
        actions.push({
          label: this.translateService.instant(`MANAGEMENT.DATASETS.DELETE`),
          icon: 'icon-delete_trash',
          click: data => {
            this.deleteClick(data);
          }
        });
      }

      columns.push({
        colId: '_actions',
        headerName: '',
        field: '',
        floatingFilter: false,
        sortable: false,
        resizable: false,
        minWidth: 64,
        suppressHeaderMenuButton: true,
        cellRenderer: 'actionCellComponent',
        cellRendererParams: {
          location: 'right',
          actions
        },
        width: 64,
        maxWidth: 64
      });
    }

    const nameField = {
      dataType: FieldDataType.Text,
      displayName: this.translateService.instant('MANAGEMENT.WORKFLOW.COLUMNS.NAME'),
      systemName: 'name',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    columns.push({
      headerName: nameField.displayName,
      field: nameField.systemName,
      colId: 'name',
      sortable: true,
      resizable: true,
      minWidth: 400,
      menuTabs: [],
      cellRenderer: canEdit ? 'linkCellComponent' : 'textCellComponent',
      cellRendererParams: canEdit
        ? {
            schemaField: nameField,
            picklistService: this.picklistService,
            clicked: data => {
              this.editClick(data);
            }
          }
        : { schemaField: nameField },
      valueGetter: getValueGetter(FieldDataType.Text)
    });
    const updatedOn = {
      dataType: FieldDataType.DateTime,
      displayName: this.translateService.instant('MANAGEMENT.WORKFLOW.COLUMNS.UPDATED-ON'),
      systemName: 'updatedOn',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    columns.push({
      headerName: updatedOn.displayName,
      field: updatedOn.systemName,
      colId: 'updatedOn',
      sortable: true,
      resizable: true,
      minWidth: 300,
      menuTabs: [],
      cellRenderer: getCellRenderer(FieldDataType.DateTime),
      cellRendererParams: {
        schemaField: updatedOn,
        picklistService: this.picklistService
      },
      valueGetter: getValueGetter(FieldDataType.DateTime)
    });

    const editedBy = {
      dataType: FieldDataType.User,
      displayName: this.translateService.instant('MANAGEMENT.WORKFLOW.COLUMNS.UPDATED-BY'),
      systemName: 'lastEditedById',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    columns.push({
      headerName: editedBy.displayName,
      field: editedBy.systemName,
      colId: 'lastEditedById',
      sortable: true,
      resizable: true,
      minWidth: 300,
      menuTabs: [],
      cellRenderer: getCellRenderer(FieldDataType.User),
      cellRendererParams: {
        schemaField: editedBy,
        organizationUId: this.organizationUId,
        nameFieldName: 'lastEditedByName',
        picklistService: this.picklistService
      },
      valueGetter: getValueGetter(FieldDataType.User)
    });

    const isActive = {
      dataType: FieldDataType.Boolean,
      displayName: this.translateService.instant('MANAGEMENT.WORKFLOW.COLUMNS.IS-ACTIVE'),
      systemName: 'isActive',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    columns.push({
      headerName: isActive.displayName,
      field: isActive.systemName,
      colId: 'isActive',
      sortable: true,
      resizable: true,
      minWidth: 300,
      menuTabs: [],
      cellRenderer: getCellRenderer(FieldDataType.Boolean),
      cellRendererParams: {
        schemaField: isActive,
        picklistService: this.picklistService,
        isEditable: true,
        click: data => {
          this.isActiveClick(data);
        }
      },
      valueGetter: getValueGetter(FieldDataType.Boolean)
    });

    const isDefault = {
      dataType: FieldDataType.Boolean,
      displayName: this.translateService.instant('MANAGEMENT.WORKFLOW.COLUMNS.IS-DEFAULT'),
      systemName: 'isDefault',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    columns.push({
      headerName: isDefault.displayName,
      field: isDefault.systemName,
      colId: 'isDefault',
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 300,
      menuTabs: [],
      cellRenderer: getCellRenderer(FieldDataType.Boolean),
      cellRendererParams: {
        schemaField: isDefault,
        picklistService: this.picklistService,
        isEditable: true,
        click: data => {
          this.isDefaultClick(data);
        }
      },
      valueGetter: getValueGetter(FieldDataType.Boolean)
    });

    return columns;
  }

  ngOnDestroy(): void {
    this.gridHelper.clearSearchFilter();
    this.destroy$.next();
    this.destroy$.complete();
  }

  newWorkflow() {
    const parentType = this.selectedParentType;
    this.store.dispatch(
      fromWorkflow.newWorkflow({
        parentType: parentType,
        name: this.translateService.instant(`MANAGEMENT.WORKFLOW.TEMPLATE-DEFAULT-NAME-${parentType}`)
      })
    );
  }

  navigate(path: string) {
    this.store.dispatch(
      fromRouting.go({
        routeData: { path: [path] }
      })
    );
  }

  editClick(workflow: Workflow) {
    this.store.dispatch(fromWorkflow.editWorkflow({ workflow }));
  }

  deleteClick(workflow: Workflow) {
    this.workflowToDelete = workflow;
  }

  isActiveClick(workflow: Workflow) {
    const active = workflow.isActive === true ? false : true;
    this.store.dispatch(fromWorkflow.setWorkflowActive({ active, workflow }));
  }

  isDefaultClick(workflow: Workflow) {
    const defaultWorkflow = workflow.isDefault === true ? false : true;
    this.store.dispatch(fromWorkflow.setWorkflowDefault({ default: defaultWorkflow, workflow }));
  }

  confirmDeleteWorkflow(response: Boolean) {
    if (response) {
      this.store.dispatch(fromWorkflow.deleteWorkflow({ workflow: this.workflowToDelete }));
      this.workflowToDelete = null;
      this.dataTable?.refreshGrid();
    } else {
      this.workflowToDelete = null;
    }
  }

  onGridDataLoaded(): void {
    this.isTableActive = true;
  }

  getWorkflowTypelRenderer = (params: ValueGetterParams) => {
    const field = params.colDef.cellRendererParams.schemaField;

    const reader = new DocumentReader(params.data);
    const value = reader.getFieldValue(field.systemName);

    return this.translateService.instant(`MANAGEMENT.WORKFLOW.PARENT-TYPE-${value}`);
  };
}
